import { selectors as browserMetaSelectors } from 'wanderu-ducks-browser-meta';
import Logger from 'wanderu-logger';
import wapiService from 'wanderu-service-wapi';

import { gtm } from '../gtm';
import { SnowplowService } from '../snowplow';
import {
    isEmailDrivenSession,
    reportEmailClickIdentify,
} from './emailClickIdentify';

const logger = Logger.getLogger('UserEvents');
let _store;

function initUserEvents(store) {
    _store = store;
}

async function getMetaData() {
    let userLocation;
    if (_store) {
        userLocation = browserMetaSelectors.selectUserLocation(_store.getState());
    } else {
        logger.warn('UserEvents not inited with redux store');
    }

    const {
        domainUserId,
        networkUserId,
    } = await SnowplowService.getIds();

    return {
        userLocation,
        domainUserId,
        networkUserId,
    };
}

async function identifyUser(userInfo) {
    const {
        source,
        identityId,
        email,
        firstName,
        lastName,
        currency,
        locale,
    } = userInfo;

    if (email) {
        gtm.dataLayer.push({
            user_data: {
                email,
                address: {
                    first_name: firstName,
                    last_name: lastName,
                },
            },
        });
    }

    // TODO this needs to be unset when user logs out
    if (identityId) {
        gtm.dataLayer.push({ user_id: identityId });
    }

    try {
        const {
            userLocation,
            domainUserId,
            networkUserId,
        } = await getMetaData();

        return await wapiService.post('events.json', {
            body: {
                event: 'user:action:identify',
                data: {
                    cognito_identity_id: identityId,
                    sp_domain_id: domainUserId,
                    sp_network_id: networkUserId,
                    email: email,
                    location: userLocation,
                    source,
                    first_name: firstName,
                    last_name: lastName,
                    user_locale: locale,
                    user_currency: currency,
                },
                event_timestamp: Date.now(),
            },
        });
    } catch (ex) {
        logger.error('identifyUser', ex);
        return null;
    }
}

async function trackEmailSignup({
    email,
    signupSource,
    locale,
    extra = {},
}) {
    if (email) {
        gtm.dataLayer.push({
            user_data: { email },
        });
    }

    try {
        // getMetaData, not getIds: the signup carries the visitor's location as well
        // as their Snowplow ids. It has to travel in the payload — the geo lookup
        // reports the visitor only when the visitor's own browser makes the request,
        // and a header carrying their IP is rewritten by the edge on the way to WAPI.
        // Same block, same source as the identify event above.
        const {
            userLocation,
            domainUserId,
            networkUserId,
        } = await getMetaData();

        return await wapiService.post('events.json', {
            body: {
                event: 'user:action:email',
                data: {
                    email,
                    location: userLocation,
                    source: 'ui-react',
                    signup_source: signupSource,
                    sp_domain_id: domainUserId,
                    sp_network_id: networkUserId,
                    user_locale: locale,
                    ...extra,
                },
                event_timestamp: Date.now(),
            },
        });
    } catch (ex) {
        logger.error('trackEmailSignup', ex);
        return null;
    }
}

async function fireCheckoutEvent(info) {
    const {
        linkType,
        identityId,
        email,
        itineraryId,
        locale,
        currency,
    } = info;

    try {
        const {
            userLocation,
            domainUserId,
            networkUserId,
        } = await getMetaData();

        return wapiService.post('events.json', {
            body: {
                event: 'user:action:checkout',
                data: {
                    cognito_identity_id: identityId,
                    sp_domain_id: domainUserId,
                    sp_network_id: networkUserId,
                    email: email,
                    location: userLocation,
                    user_locale: locale,
                    user_currency: currency,
                    link_type: linkType,
                    itinerary_id: itineraryId,
                },
                event_timestamp: Date.now(),
            },
        });
    } catch (ex) {
        logger.error('fireCheckoutEvent', ex);
        return null;
    }
}

/**
 * Reports an email-driven session if this page load came from a tracked Blueshift
 * email link, so Blueshift can stitch the anonymous Snowplow cookie to the recipient.
 * Thin wiring over `reportEmailClickIdentify` — supplies the real `identifyUser`.
 *
 * Guards on the query string first: almost every page load is not email-driven and
 * should cost a single read. Returns the outcome (or null when there was nothing to
 * do) so the caller decides what deserves a log line.
 *
 * `search` must be the query string captured at page load, not read here: this fires
 * once the Snowplow tracker is ready, which can be up to 30s later for a first-time
 * visitor, and any navigation in between drops `bsft_uid` from the live URL.
 *
 * See wanderu/strategy#418 and wanderu/ui-react#1091.
 */
function identifyEmailClickSessionIfPresent({ locale, currency, search } = {}) {
    if (!isEmailDrivenSession(search)) {
        return Promise.resolve(null);
    }

    return reportEmailClickIdentify({ currency, identify: identifyUser, locale });
}

export {
    fireCheckoutEvent,
    identifyEmailClickSessionIfPresent,
    identifyUser,
    initUserEvents,
    trackEmailSignup,
};
